// The Feedback HTTP client (packages/shared/src/http/feedback.ts; Slice 6; D45; ADR-0023).
// Every response parses through the SAME shared Zod schema apps/api validates against
// (app-engineering §2 "parse, don't validate"), matching src/api/money.ts's/pulse.ts's own
// established convention: inline the literal path string (`FEEDBACK_ROUTES`'s own template,
// http/feedback.ts:14-17), never hand-roll a wire shape a shared schema already defines.
//
// LANDED (`FEEDBACK_ROUTES`, http/feedback.ts:14-17; `FeedbackController`,
// apps/api/src/feedback/feedback.controller.ts:44-90): `overview` (GET, the D45 read
// surface - themes, top requests, the escalation state) and `submit` (POST, one
// founder-supplied item: founder_note | email | import - never the in-app `widget` beacon).
import { z } from "zod";
import { FeedbackOverview, FeedbackSubmission, FeedbackSubmitRequestBody, FeedbackThemeId, FeatureRequest, FeatureRequestId } from "@nightcrew/shared";
import type { AppId, Result } from "@nightcrew/shared";
import { apiFetch, apiPost } from "./client.js";
import type { ClientError } from "./errors.js";

// ============================================================================
// LANDED (http/feedback.ts:14-17, 26-49)
// ============================================================================

/** GET /v1/apps/:appId/feedback-overview (http/feedback.ts:15,26-27). */
export const getFeedbackOverview = (appId: AppId): Promise<Result<FeedbackOverview, ClientError>> =>
  apiFetch(`/v1/apps/${appId}/feedback-overview`, FeedbackOverview);

/** POST /v1/apps/:appId/feedback (http/feedback.ts:16,39-49) - a founder-supplied item
 * (`source` is WIDENED to `founder_note | email | import`, never the widget beacon). */
export const submitFeedback = (
  appId: AppId,
  body: FeedbackSubmitRequestBody,
): Promise<Result<FeedbackSubmission, ClientError>> =>
  apiPost(`/v1/apps/${appId}/feedback`, FeedbackSubmission, body);

// ============================================================================
// PROPOSED (no landed backing - flagged, not invented; the ux spec's own state table,
// slice6-ux-flows.md §5.6, explicitly names each verb+path below, exactly the same
// "built against the UX spec's own explicitly proposed shape" precedent src/api/
// money.ts's `changeCeiling` already established. `FeedbackController` (verified this
// pass) registers exactly two routes - `feedback-overview` and `feedback` - so every
// call below will 404 against the real API until the tech-lead/backend-engineer lands
// the route; each caller's own screen/component handles that as an honest error state
// (never a silent misread), matching `changeCeiling`'s own "Save fails, shows a retry"
// precedent, not `src/api/pulse.ts`'s "omit the call entirely" one (that lane declines
// because the UX spec proposes NO shape for the gap it names - this spec does).
// ============================================================================

/** PROPOSED GET /v1/apps/:appId/feedback/themes/:themeId/history (ux spec §5.6's own
 * state-table row, "Closed loop present") - the before/after window counts
 * `ClosedLoopCallout` needs. No `feedback_theme_history`-style table is confirmed landed
 * (component-designs.md §9's own open item) - this response shape is this lane's
 * reasoned, minimal guess at what such a table would need to answer "before vs after,"
 * flagged for the tech-lead to confirm or correct. */
export const FeedbackThemeHistory = z.object({
  before: z.int().nonnegative(),
  beforeWindow: z.string(),
  after: z.int().nonnegative(),
  afterWindow: z.string(),
});
export type FeedbackThemeHistory = z.infer<typeof FeedbackThemeHistory>;

export const getFeedbackThemeHistory = (
  appId: AppId,
  themeId: FeedbackThemeId,
): Promise<Result<FeedbackThemeHistory, ClientError>> =>
  apiFetch(`/v1/apps/${appId}/feedback/themes/${themeId}/history`, FeedbackThemeHistory);

/** PROPOSED POST /v1/apps/:appId/feedback/requests/:requestId/vote (ux spec §5.6's own
 * state-table row, "Voting - Studio/Business") - Studio+ only server-side (D36); this
 * slice stores `votes` only, the voting workflow/feature-lane handoff is Slice 8
 * (ADR-0023 deferral). Returns the updated `FeatureRequest` (the new `votes` count). */
export const voteForRequest = (
  appId: AppId,
  requestId: FeatureRequestId,
): Promise<Result<FeatureRequest, ClientError>> =>
  apiPost(`/v1/apps/${appId}/feedback/requests/${requestId}/vote`, FeatureRequest, {});

/** PROPOSED POST /v1/apps/:appId/feedback/imports (ux spec §5.6's own state-table row,
 * "Adding founder feedback - import, uploading") - multipart, mirroring
 * `uploadValidateContext`'s FormData pattern (src/api/validate.ts) exactly, the platform's
 * established shape for a hardened, scanned upload. `state`/`scan` reuse the SAME
 * vocabulary `ValidateUploadResult` already established (model/validate.ts:171-189:
 * `state: "accepted" | "rejected"`, `scan` a closed `blocked_malware|blocked_structure|
 * blocked_content` set on rejection) rather than inventing fresh wording for the
 * identical concept - a rejected file is a SUCCESSFUL response, never an error branch. */
export const FeedbackImportResult = z.discriminatedUnion("state", [
  z.object({ state: z.literal("accepted"), submission: FeedbackSubmission }),
  z.object({
    state: z.literal("rejected"),
    scan: z.enum(["blocked_malware", "blocked_structure", "blocked_content"]),
  }),
]);
export type FeedbackImportResult = z.infer<typeof FeedbackImportResult>;

export const importFeedback = (
  appId: AppId,
  file: File,
): Promise<Result<FeedbackImportResult, ClientError>> => {
  const form = new FormData();
  form.set("filename", file.name);
  form.set("mime", file.type);
  form.set("declaredSize", String(file.size));
  form.set("file", file);
  return apiFetch(`/v1/apps/${appId}/feedback/imports`, FeedbackImportResult, {
    method: "POST",
    body: form,
  });
};
